/* eslint-disable react/jsx-handler-names */
/**
 * @fileOverview The main entry point to your standalone. Start editing here.
 */

import React from 'react'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/reset.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/site.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/segment.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/container.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/header.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/image.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/list.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/grid.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/dropdown.css'
import './styles.scss'
import { Masthead } from '@ta-interaktiv/react-masthead'
import { Footer } from '@ta-interaktiv/react-footer'
import { ThemeSwitch } from '@ta-interaktiv/react-theme-switch'
import { browserCheck } from '@ta-interaktiv/browsercheck'
import { MunicipalitySearch } from '@ta-interaktiv/react-municipality-search'
import { SwissMap } from '@ta-interaktiv/react-swissmap'
import { ActiveMunicipality } from './active-municipality'

import { Dropdown } from 'semantic-ui-react'
import { RankingTable } from './ranking-table'
import { ColorLegend } from './color-legend'
import * as d3 from 'd3'
import { thousandsSeparators } from 'lib/thousandsSeperator'
import { parseHostname } from '@ta-interaktiv/parse-hostname'

const tenant = parseHostname().publicationName
const isMobile = browserCheck.isMobile

// GET PARAMS FROM URL
const params = new URL(window.location).searchParams
const municipalityPreselect = {
  GDENR: parseInt(params.get('gemeinde'))
}

const ARTICLE_ID = '478918908908'
const taxSubjects = [
  'Ledig, 0 Kinder',
  'Ledig, 1 Kind',
  'Ledig, 2 Kinder',
  'Ledig, 3 Kinder',
  'Verh. ein Einkommen, 0 Kinder',
  'Verh. ein Einkommen, 1 Kind',
  'Verh. ein Einkommen, 2 Kinder',
  'Verh. ein Einkommen, 3 Kinder',
  'Verh. zwei Einkommen (70/30), 0 Kinder',
  'Verh. zwei Einkommen (70/30), 1 Kind',
  'Verh. zwei Einkommen (70/30), 2 Kinder',
  'Verh. zwei Einkommen (70/30), 3 Kinder',
  'Verh. zwei Einkommen (50/50), 0 Kinder',
  'Verh. zwei Einkommen (50/50), 1 Kind',
  'Verh. zwei Einkommen (50/50), 2 Kinder',
  'Verh. zwei Einkommen (50/50), 3 Kinder',
  'Rentner:in, ledig',
  'Verh. eine Rente',
  'Verh. zwei Renten (70/30)',
  'Verh. zwei Renten (50/50)'
]
const availableIncome = [
  { text: "15'000", value: 15000 },
  { text: "20'000", value: 20000 },
  { text: "25'000", value: 25000 },
  { text: "30'000", value: 30000 },
  { text: "35'000", value: 35000 },
  { text: "40'000", value: 40000 },
  { text: "45'000", value: 45000 },
  { text: "50'000", value: 50000 },
  { text: "60'000", value: 60000 },
  { text: "70'000", value: 70000 },
  { text: "80'000", value: 80000 },
  { text: "90'000", value: 90000 },
  { text: "100'000", value: 100000 },
  { text: "125'000", value: 125000 },
  { text: "150'000", value: 150000 },
  { text: "175'000", value: 175000 },
  { text: "200'000", value: 200000 },
  { text: "250'000", value: 250000 },
  { text: "300'000", value: 300000 },
  { text: "400'000", value: 400000 },
  { text: "500'000", value: 500000 },
  { text: "750'000", value: 750000 },
  { text: '1 Million', value: 1000000 },
  { text: '5 Millionen', value: 5000000 },
  { text: '10 Millionen', value: 10000000 },
  { text: '50 Millionen', value: 50000000 },
  { text: '100 Millionen', value: 100000000 }
]

const colorPalette = ['#296b8e', 'lightgrey', '#9e272f']
const titleSentences = {
  heaven: 'Familien wohnen eher steuergünstig in ',
  earth: 'So viel Steuern zahlen Sie in ',
  hell: 'Vergleichsweise hohe Steuerrechnung in '
}
const introSentences = {
  heaven: 'ist dem Steuerparadies näher als der -hölle.',
  earth: 'liegt im Mittelfeld zwischen Steuerparadies und -hölle.',
  hell: 'ist der Steuerhölle näher als dem -paradies.'
}

export class AppComponent extends React.Component {
  ToolTip = ({ activeData }) => {
    let cantonInName = ''
    const cantonPattern = /\([A-Z][A-Z]\)/
    if (activeData) {
      cantonInName = activeData.GemeindenameDE.match(cantonPattern)
    }

    return (
      <>
        {activeData ? (
          <div className='ui raised compact segment inner-tooltip'>
            <div>
              <div className='municipality-name'>
                {activeData.GemeindenameDE}{' '}
                {!cantonInName ? '(' + activeData.Kanton + ')' : ''}
              </div>
              <div className='percentage'>
                Belastung:
                <span className='number'>{activeData.Belastung + '%'}</span>
              </div>
              <div className='amount'>
                Fällige Steuer:
                <span className='number'>
                  {thousandsSeparators(
                    (
                      (activeData.Belastung / 100) *
                      this.state.selectedIncome
                    ).toFixed(2)
                  ) + ' CHF'}
                </span>
              </div>
              <div className='rank'>
                Rang <span>{thousandsSeparators(activeData.Rank)}</span>/
                {this.state.lastRank}
              </div>
            </div>
          </div>
        ) : (
          <div className='ui raised compact segment inner-tooltip'>
            <div>Keine Daten vorhanden</div>
          </div>
        )}
      </>
    )
  }

  constructor() {
    super()
    this.state = {
      preselectedMunicipality: undefined,
      activeMunicipality: undefined,
      searchToggle: -1,
      municipalityData: undefined,
      taxMinValue: 1,
      taxMaxValue: 2,
      selectedTaxSubject: 15,
      selectedIncome: 150000,
      colorFunction: undefined,
      selectedAutoText: undefined,
      lastRank: 2206
    }
    this.preselectMunicipality = this.preselectMunicipality.bind(this)
  }

  componentDidMount() {
    // redirect to newer article
    tenant &&
      (window.location.href =
        'https://www.' +
        tenant +
        '.ch/steuerparadies')

    this.fetchData()
  }

  componentDidUpdate = (_prevProps, prevState) => {
    if (
      prevState.selectedTaxSubject !== this.state.selectedTaxSubject ||
      prevState.selectedIncome !== this.state.selectedIncome
    ) {
      this.fetchData(prevState?.activeMunicipality?.bfsId)
    }
  }

  preselectMunicipality(preselection) {
    this.setState(
      {
        preselectedMunicipality: this.state.municipalityData.find(
          muni => +muni.bfsId === +preselection.GDENR
        )
      },
      function() {
        const lang = 'de'
        fetch(
          'https://interaktiv.tagesanzeiger.ch/2022/steuerparadies/auto-text/autotext_2021_15_150000_' +
            lang +
            '.json'
        ).then(res => {
          res.json().then(rawData => {
            if (this.state.preselectedMunicipality) {
              this.setState({
                selectedAutoText: rawData.find(
                  autoTextObj =>
                    +autoTextObj.bfsid ===
                    +this.state.preselectedMunicipality.bfsId
                )
              })
            }
          })
        })
      }
    )
    if (this.state.activeMunicipality === undefined) {
      this.setState({
        activeMunicipality: this.state.municipalityData.find(
          muni => +muni.bfsId === +preselection.GDENR
        )
      })
    }
  }

  fetchData = prevActiveBfsId => {
    fetch(
      'https://interaktiv.tagesanzeiger.ch/2022/steuerparadies/steuerbelastung-data/2021_' +
        this.state.selectedTaxSubject +
        '_' +
        this.state.selectedIncome +
        '.json'
    ).then(res => {
      res.json().then(rawData => {
        const formattedData = rawData

        formattedData.municipalities = rawData.municipalities.map(
          municipality => {
            municipality.bfsId = municipality.index
            delete municipality.index // Delete old key
            return municipality
          }
        )
        this.setState({
          municipalityData: formattedData.municipalities,
          taxMinValue: formattedData.extent[0],
          taxMaxValue: formattedData.extent[1],
          lastRank: Math.max(
            ...formattedData.municipalities.map(muni => muni.Rank)
          ),
          colorFunction: d3
            .scaleLinear()
            .domain([
              formattedData.extent[0],
              (formattedData.extent[0] + formattedData.extent[1]) / 2,
              formattedData.extent[1]
            ])
            .range(colorPalette)
        })
        if (prevActiveBfsId) {
          this.setState({
            activeMunicipality: formattedData.municipalities.find(
              muni => muni.bfsId === prevActiveBfsId
            )
          })
        }
        if (this.state.preselectedMunicipality === undefined) {
          this.preselectMunicipality(municipalityPreselect)
        }
      })
    })
  }

  render() {
    console.log(this.state)

    return (
      <div className='index'>
        <Masthead articleId={ARTICLE_ID} />
        <ThemeSwitch />

        <header className='ui vertical  very fitted  segment'>
          <div className='ui container'>
            <div className='ui header'>
              {!this.state.preselectedMunicipality && (
                <>
                  <h1>Hier finden Sie Ihr Steuerparadies</h1>
                  <h2>
                    Wo zahlen Sie mit Ihrem Einkommen am wenigsten Steuern? Und
                    wo am meisten? Unsere interaktive Karte zeigt es in wenigen
                    Sekunden.
                  </h2>
                </>
              )}
              {this.state.preselectedMunicipality &&
                this.state.selectedAutoText && (
                  <>
                    <h1>
                      {titleSentences[this.state.selectedAutoText.hellorheaven]}
                      {this.state.selectedAutoText.namemunicipality}
                    </h1>
                    <h2>
                      Wenig in {this.state.selectedAutoText.taxheavenincanton},
                      viel in {this.state.selectedAutoText.taxhellincanton}: Im
                      Kanton {this.state.selectedAutoText.namecanton} variiert
                      die Steuerbelastung je nach Wohnort.{' '}
                      {this.state.selectedAutoText.hellorheaven === 'earth' ? (
                        <>
                          {this.state.selectedAutoText.namemunicipality} liegt
                          im Mittelfeld. Machen Sie den kantonalen Vergleich.
                        </>
                      ) : (
                        <>
                          Finden Sie heraus, wo Sie weniger und wo mehr Steuern
                          zahlen müssten.
                        </>
                      )}
                    </h2>
                  </>
                )}
            </div>
          </div>
        </header>

        <div className='ui vertical segment'>
          <div className='ui text container'>
            <div className='ta metainfo'>
              <div className='ta author'>
                Iwan Städler, Dariush Mehdiaraghi, Timo Grossenbacher, Mathias
                Lutz, Jonas Liniger
              </div>
              <div className='ta updated'>Aktualisiert am 10. Februar 2022</div>
            </div>
            <div className='ta text' style={{ marginBottom: '1em' }}>
              <p>
                Vergleichen lohnt sich – nicht nur für Einkommensmillionäre.
                Auch bei tieferen Salären fällt die Steuerbelastung je nach
                Wohnort höchst unterschiedlich aus. Im Extremfall lassen sich
                mit einem Umzug mehr als 80 Prozent der Steuern sparen.
              </p>
            </div>
          </div>

          {this.state.preselectedMunicipality && this.state.selectedAutoText && (
            <div className='ui container ta dynamic text'>
              <p>
                <span className='accent'>
                  {this.state.selectedAutoText.namemunicipality}
                </span>{' '}
                {introSentences[this.state.selectedAutoText.hellorheaven]}{' '}
                Arbeitende Paare mit zwei Kindern, die pro Jahr 150'000 Franken
                verdienen, zahlen in{' '}
                {this.state.selectedAutoText.namemunicipality}{' '}
                <span className='accent'>
                  {this.state.selectedAutoText.taxloadrelativeformunicipality}{' '}
                  Prozent
                </span>{' '}
                ihres Jahreseinkommens als Steuern, was{' '}
                <span className='accent'>
                  {thousandsSeparators(
                    this.state.selectedAutoText.taxloadabsoluteformunicipality
                  )}{' '}
                  Franken
                </span>{' '}
                entspricht. Damit liegt{' '}
                {this.state.selectedAutoText.namemunicipality} auf{' '}
                <span className='accent'>
                  Platz{' '}
                  {thousandsSeparators(
                    this.state.selectedAutoText.ranktaxloadabsolutecantonal
                  )}{' '}
                  von {this.state.selectedAutoText.countmunicipalitiesincanton}
                </span>{' '}
                der steuergünstigsten Wohnorte im Kanton{' '}
                {this.state.selectedAutoText.namecanton}.{' '}
                {+this.state.selectedAutoText.ranktaxloadabsolutecantonal !==
                  1 && (
                  <>
                    Das Steuerparadies befindet sich in{' '}
                    {this.state.selectedAutoText.taxheavenincanton}, wo oben
                    genannte Familie nur{' '}
                    {thousandsSeparators(
                      this.state.selectedAutoText.taxloadabsoluteforheaven
                    )}{' '}
                    Franken Steuern zahlen würde.{' '}
                  </>
                )}
                {this.state.selectedAutoText.ranktaxloadabsolutecantonal !==
                  this.state.selectedAutoText.countmunicipalitiesincanton && (
                  <>
                    Die figurative Steuerhölle befindet sich dagegen in{' '}
                    {this.state.selectedAutoText.taxhellincanton}, wo für die
                    Familie{' '}
                    {thousandsSeparators(
                      this.state.selectedAutoText.taxloadabsoluteforhell
                    )}{' '}
                    Franken Steuern fällig wären.
                  </>
                )}
              </p>
            </div>
          )}

          <div className='ui text container'>
            <div className='ta text'>
              <p>
                Doch aufgepasst: Das Steuerparadies liegt nicht für alle am
                selben Ort. Je nach Einkommen und Haushaltstyp ist eine andere
                Gemeinde am günstigsten. Dank unserer interaktiven Karte finden
                Sie mit wenigen Klicks heraus, wo Sie am wenigsten Steuern
                zahlen. Wählen Sie zwischen 20 verschiedenen Haushaltstypen und
                27 Einkommensklassen – und schon entdecken Sie Ihr persönliches
                Steuerparadies und Ihre Steuerhölle.
              </p>
            </div>

            <div className='example ta text'>
              <h5>Lesebeispiel</h5>
              <p>
                In der <b>Stadt Zürich</b> zahlt eine{' '}
                <b>ledige Person ohne Kinder</b> mit einem{' '}
                <b>Bruttoeinkommen von 100'000 Franken</b> bei{' '}
                <b>12,62% Steuerbelastung</b> rund{' '}
                <b>12'620 Franken Kantons-, Gemeinde- und Kirchensteuern</b>.
              </p>
              <p>Je nach Abzügen kann dieser Betrag variieren.</p>
              <p className='disclaim'>
                Für die Berechnung nicht berücksichtigt wurden die
                Vermögenssteuer sowie die Bundessteuer, welche ohnehin in allen
                Gemeinden gleich hoch ist.
              </p>
            </div>
          </div>
        </div>

        <div
          className='ui vertical grey segment'
          style={{ position: 'inherit' }}
        >
          <div className='ui graphic container'>
            <h3>Wo liegt Ihr Steuerparadies?</h3>
            <div
              className='ui stackable two column grid'
              style={{ marginBottom: '0.8em' }}
            >
              <div className='column'>
                <label htmlFor='taxSubject'>
                  Wählen Sie das Berechnungsmodell
                </label>
                {/* TODO: format number from 10000 to 10'000 */}
                <Dropdown
                  name='taxSubject'
                  id='taxSubject'
                  value={this.state.selectedTaxSubject}
                  fluid
                  selection
                  onChange={(event, item) => {
                    this.setState({ selectedTaxSubject: item.value })
                  }}
                  options={taxSubjects.map((taxSubject, index) => ({
                    key: taxSubject,
                    text: taxSubject,
                    value: index + 1
                  }))}
                />
              </div>
              <div className='column'>
                <label htmlFor='income'>
                  und das jährliche Bruttoeinkommen in Franken
                </label>
                <Dropdown
                  name='income'
                  id='income'
                  value={this.state.selectedIncome}
                  fluid
                  selection
                  onChange={(event, item) => {
                    this.setState({ selectedIncome: item.value })
                  }}
                  options={availableIncome.map((income, index) => ({
                    key: index,
                    text: income.text + ' CHF',
                    value: income.value
                  }))}
                />
              </div>
            </div>
            <div className={isMobile ? '' : 'ui container float-above'}>
              <div className={isMobile ? '' : 'municipality-search-wrapper'}>
                <div className='input-wrapper'>
                  <label htmlFor='search'>Suchen Sie nach einer Gemeinde</label>
                  <MunicipalitySearch
                    municipalityData='20200101'
                    locale='de'
                    dedupe
                    hideTooltip
                    resetOnSelect
                    placeholder='Gemeindesuche'
                    showLastSelectedMunicipalities
                    numberOfLastSelectedMunicipalities={1}
                    onSelectionHandler={selectedMunicipality => {
                      if (this.state.municipalityData) {
                        this.setState({
                          activeMunicipality: this.state.municipalityData.find(
                            muni => +muni.bfsId === +selectedMunicipality.GDENR
                          )
                        })
                      }
                    }}
                  />
                </div>
                {!isMobile && this.state.activeMunicipality && (
                  <ActiveMunicipality
                    activeMunicipality={this.state.activeMunicipality}
                    selectedIncome={this.state.selectedIncome}
                    lastRank={this.state.lastRank}
                  />
                )}
              </div>
            </div>
            {/* <ToolTip /> */}
            {this.state.municipalityData && (
              <SwissMap
                propertyNameToCompare='Belastung'
                municipalityData={this.state.municipalityData}
                minValue={this.state.taxMinValue}
                maxValue={this.state.taxMaxValue}
                municipalitySearchYear='20200101'
                hideMunicipalitySearch
                colorPalette={colorPalette}
                topoJsonYear={2020}
                activeMunicipalityId={
                  this.state.activeMunicipality?.bfsId || false
                }
                hoverHandler={newData => {
                  // console.log(newData)
                }}
                newActiveMunicipalityIdHandler={newId => {
                  this.setState({
                    activeMunicipality: this.state.municipalityData.find(
                      muni => +muni.bfsId === +newId
                    )
                  })
                }}
                ToolTip={this.ToolTip}
                activeStrokeWidth={isMobile ? '4' : '2'}
              />
            )}
            {this.state.taxMaxValue && (
              <div className='ui text container'>
                <ColorLegend
                  colorPalette={colorPalette}
                  minVal={this.state.taxMinValue}
                  maxVal={this.state.taxMaxValue}
                  currentValue={this.state.activeMunicipality?.Belastung}
                />
              </div>
            )}
            {isMobile && this.state.activeMunicipality && (
              <ActiveMunicipality
                activeMunicipality={this.state.activeMunicipality}
                selectedIncome={this.state.selectedIncome}
                lastRank={this.state.lastRank}
              />
            )}
          </div>
          {this.state.municipalityData && (
            <div className={'ui text ' + (isMobile ? '' : 'container')}>
              <RankingTable
                municipalityData={this.state.municipalityData}
                colorPalette={colorPalette}
                minValue={this.state.taxMinValue}
                maxValue={this.state.taxMaxValue}
                activeMunicipalityId={
                  this.state.activeMunicipality?.bfsId || false
                }
                onSelectionHandler={newId =>
                  this.setState({
                    activeMunicipality: this.state.municipalityData.find(
                      muni => +muni.bfsId === +newId
                    )
                  })
                }
              />
            </div>
          )}
        </div>

        <div className='ui vertical segment'>
          <div className='ui text container'>
            <div className='ta text'>
              <p>
                Die Daten sind amtlich. Die Eidgenössische Steuerverwaltung hat
                sie vor kurzem{' '}
                <a
                  href='https://swisstaxcalculator.estv.admin.ch/#/taxburden/income-wealth-tax'
                  target='_blank'
                  className='cbLink cB'
                  rel='noreferrer'
                >
                  für das Jahr 2021
                </a>{' '}
                zugänglich gemacht. Was gilt es dabei zu beachten? Und welches
                sind die interessantesten Erkenntnisse? Neun Fragen und
                Antworten.
              </p>

              <div className='CrossheadElement'>
                <div className='num'>1</div>
                <h4>
                  Wie gross sind die Unterschiede zwischen den einzelnen
                  Wohnorten?
                </h4>
              </div>
              <p className='ta text'>
                Sehr gross. Nehmen wir eine Familie mit zwei Kindern, bei
                welcher Mann und Frau je die Hälfte des Bruttoeinkommens von
                150’000 Franken verdienen. In den neuenburgischen Gemeinden
                Enges und Les Verrières muss diese Familie dem Fiskus 14,2
                Prozent ihres Einkommens abliefern. Zieht sie nach Baar im
                Kanton Zug, sind es nur noch 2,2 Prozent. Mit dem
                Wohnortswechsel verringert die Familie ihre Steuern also um rund
                85 Prozent respektive 18’000 Franken. Auch ein Umzug innerhalb
                der Sprachgrenze kann einen grossen Unterschied machen: Wechselt
                die besagte Familie etwa von Holderbank SO nach Baar, fällt ihre
                Steuerrechnung um gut 17’000 Franken geringer aus.
              </p>

              <div className='CrossheadElement'>
                <div className='num'>2</div>
                <h4>
                  Fahren die Spitzenverdiener ebenfalls in Baar am günstigsten?
                </h4>
              </div>
              <p className='ta text'>
                Nein, die allerhöchsten Einkommen werden in den Ausserschwyzer
                Gemeinden Feusisberg, Freienbach und Wollerau am
                zurückhaltendsten besteuert. Je nach Haushaltstyp sind diese
                drei Gemeinden ab einem Jahreseinkommen von 250’000 bis 5
                Millionen Franken am günstigsten. Eine Doppelverdienerfamilie
                mit zwei Kindern und 5 Millionen Franken Einkommen muss dort
                983’000 Franken an den Fiskus abliefern – 1’035’000 Franken
                weniger als mit demselben Einkommen in der Genfer Gemeinde
                Avully. Da lohnt sich ein Umzug – zumindest finanziell. Für
                dieses Jahr werden die Steuern im Kanton Schwyz gar noch tiefer
                ausfallen. Denn der Kantonsrat hat im vergangenen Dezember den
                Steuerfuss um ein Fünftel gesenkt.
              </p>

              <div className='CrossheadElement'>
                <div className='num'>3</div>
                <h4>Wo zahlen Wenigverdiener am wenigsten Steuern?</h4>
              </div>
              <p className='ta text'>
                Vielerorts. Etliche Kantone und Gemeinden lassen sehr tiefe
                Einkommen unversteuert. So zahlt zum Beispiel die erwähnte
                Doppelverdienerfamilie bis zu einem Einkommen von 60’000 Franken
                in den Kantonen Basel-Stadt, Baselland, Graubünden und Zug keine
                Einkommenssteuern. In Zug ist dies selbst bei einem Einkommen
                von 90’000 Franken noch der Fall.
              </p>

              <div className='CrossheadElement'>
                <div className='num'>4</div>
                <h4>Wer schont die Armen und schröpft die Reichen?</h4>
              </div>
              <p className='ta text'>
                Der Kanton Genf ist beim unteren Mittelstand günstig, besteuert
                aber die Spitzenverdiener stark.
              </p>

              <div className='CrossheadElement'>
                <div className='num'>5</div>
                <h4>Wer schröpft die Armen und schont die Reichen?</h4>
              </div>
              <p className='ta text'>
                Zunächst: Kein Kanton und keine Gemeinde belastet die Ärmeren
                stärker als die Spitzenverdiener. Dies wäre verfassungswidrig.
                Aber im Vergleich zu anderen Kantonen ist Appenzell Innerrhoden
                für Reiche günstig und für Arme eher teuer. Zug wiederum ist bei
                allen Einkommensklassen günstig. Und Bern ist für die meisten
                teuer.
              </p>

              <div className='CrossheadElement'>
                <div className='num'>6</div>
                <h4>Sind Steuerparadiese auch für Wenigverdiener attraktiv?</h4>
              </div>
              <p className='ta text'>
                Nein. Es ist zwar möglich, dass Wenigverdiener bei den Steuern
                ein paar Franken sparen, wenn sie in ein Steuerparadies ziehen.
                Viel stärker ins Gewicht fällt bei den unteren Einkommensklassen
                aber die Miete. Berücksichtigt man die Wohnkosten, kann sich ein
                Steuerparadies als ziemlich teuer erweisen. Bei tiefen Einkommen
                sind Steuerhöllen unter dem Strich oft günstiger. Denn wo hohe
                Steuern verlangt werden, sind in der Regel die Mieten tief – und
                umgekehrt.
              </p>

              <div className='CrossheadElement'>
                <div className='num'>7</div>
                <h4>Welche Steuern sind berücksichtigt und welche nicht?</h4>
              </div>
              <p className='ta text'>
                Es geht hier um die Einkommenssteuer, die bei den meisten
                Haushalten den Grossteil der Fiskallast ausmacht. Unsere Zahlen
                umfassen sowohl die Einkommenssteuern des Bundes als auch jene
                von Kanton und Gemeinde. Nicht enthalten sind dagegen allfällige
                Kirchensteuern, sie hängen von der Konfession ab. Ebenso wenig
                eingerechnet sind die Vermögenssteuer sowie anderweitige Steuern
                und Gebühren.{' '}
                <a
                  href='https://www.estv.admin.ch/dam/estv/de/dokumente/allgemein/steuerrechner/Dokument2.pdf'
                  target='_blank'
                  className='cbLink cB'
                  rel='noreferrer'
                >
                  Erläuterungen zu den Steuerbelastungsstatistiken der
                  Eidgenössischen Steuerverwaltung
                </a>
              </p>

              <div className='CrossheadElement'>
                <div className='num'>8</div>
                <h4>
                  Was ist mit den Abzügen, die ich in der Steuererklärung
                  anbringen kann – wurden die mitberechnet?
                </h4>
              </div>
              <p className='ta text'>
                Alle gesetzlichen Abzüge, für die man keinen Nachweis erbringen
                muss, sind berücksichtigt. Nicht eingerechnet sind dagegen
                Abzüge, die von den effektiven Kosten abhängen. Weil diese je
                nach Haushalt unterschiedlich ausfallen, fliessen sie nicht in
                die Rechnung ein. Folglich kann Ihre individuelle
                Steuerbelastung vom hier ausgewiesenen Resultat abweichen. Für
                den Vergleich zwischen den verschiedenen Gemeinden spielt dies
                aber keine grosse Rolle.
              </p>

              <div className='CrossheadElement'>
                <div className='num'>9</div>
                <h4>Welches ist die höchste ausgewiesene Fiskallast?</h4>
              </div>
              <p className='ta text'>
                46 Prozent. So viel müsste ein Rentner mit einem Einkommen von
                100 Millionen Franken in der Genfer Gemeinde Avully zahlen. Aber
                ein Rentner kommt kaum auf ein Einkommen von 100 Millionen. Und
                wenn, dann wohnt er garantiert nicht in Avully.
              </p>
            </div>
          </div>
        </div>

        <Footer
          articleId={ARTICLE_ID}
          hidePortfolioTeaser
          questions={[
            'Wie informativ war der Beitrag?',
            'Funktionierte die technische Umsetzung?',
            'Wie gefällt Ihnen die Gestaltung?'
          ]}
          credits={[
            ['Text und Analyse', 'Iwan Städler'],
            [
              'Konzept und Programmierung',
              'Dariush Mehdiaraghi und Mathias Lutz, Interaktiv-Team'
            ],
            ['Datenaufbereitung', 'Jonas Liniger und Timo Grossenbacher'],
            [
              'Quelle',
              '<a href="https://swisstaxcalculator.estv.admin.ch/#/taxburden/income-wealth-tax" target="_blank">Eidgenössische Steuerverwaltung</a>'
            ]
          ]}
        />
      </div>
    )
  }
}
